import type { ResearchRecord, ResearchStatus } from "@pokifolio/contracts";
import { Link } from "@tanstack/react-router";
import { useState } from "react";
import {
  ArrowRight,
  CheckCircle,
  ClipboardList,
  FilePen,
  Hourglass,
  Library,
  type LucideIcon,
  Plus,
  Search,
  XCircle,
} from "lucide-react";

import { PageHeader } from "../../components/page-header";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { Card, CardDescription, CardTitle } from "../../components/ui/card";
import { Skeleton } from "../../components/ui/skeleton";
import { useCurrentUser, useHasPermission } from "../auth/session";
import { describeCardStep } from "../cards/card-format";
import { CardStatsPanel } from "../cards/card-stats-panel";
import { useCardList } from "../cards/card-queries";
import { AddCardDialog } from "../cards/cards-page";
import { formatMoney } from "../search/format";
import { RESEARCH_STATUS_LABELS, RESEARCH_STATUS_TONES, formatDate } from "../research/format";
import { useResearchList, useResearchSummary } from "../research/research-queries";

const MY_STEPS: readonly { status: ResearchStatus; label: string; hint: string; icon: LucideIcon }[] = [
  { status: "draft", label: "Drafts", hint: "Being checked", icon: FilePen },
  { status: "waiting_approval", label: "Waiting for approval", hint: "Sent to the admin", icon: Hourglass },
  { status: "approved", label: "Approved", hint: "Ready to buy", icon: CheckCircle },
  { status: "rejected", label: "Rejected", hint: "Revise and send again", icon: XCircle },
];

const RECENT = 5;

export function DashboardPage() {
  const { user, permissions } = useCurrentUser();
  const canSearch = useHasPermission("search.run");
  const canResearch = useHasPermission("research.create");
  const canApprove = useHasPermission("research.approve");
  const canSeePortfolio = useHasPermission("holdings.view");
  const summary = useResearchSummary(canResearch);
  const firstName = user.name.split(" ")[0] ?? user.name;
  const mine = summary.data?.mine;
  const waiting = summary.data?.waitingApproval ?? 0;
  const [adding, setAdding] = useState(false);

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title={`Welcome, ${firstName}`}
        description={`You are signed in as ${user.role === "admin" ? "an admin" : "an employee"} with ${String(permissions.length)} permissions.`}
        actions={
          <>
            {canResearch && (
              <Button onClick={() => setAdding(true)}>
                <Plus aria-hidden="true" />
                Add card
              </Button>
            )}
            {canSearch && (
              <Button variant="secondary" asChild>
                <Link to="/search">
                  <Search aria-hidden="true" />
                  New search
                </Link>
              </Button>
            )}
          </>
        }
      />
      <AddCardDialog open={adding} onOpenChange={setAdding} />

      {canSearch && canResearch && (
        <section aria-labelledby="my-research" className="flex flex-col gap-3">
          <h2
            id="my-research"
            className="text-sm font-semibold uppercase tracking-wide text-muted-foreground"
          >
            My research
          </h2>
          <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
            {MY_STEPS.map(({ status, label, hint, icon: Icon }) => (
              <Link
                key={status}
                to="/research"
                search={{ scope: "mine", status }}
                aria-label={mine === undefined ? label : `${String(mine[status])} ${label}`}
                className="group rounded-xl border bg-card p-5 text-card-foreground shadow-sm transition-colors hover:bg-accent/50"
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="text-sm font-medium">{label}</span>
                  <Icon className="size-4 text-muted-foreground" aria-hidden="true" />
                </div>
                {mine === undefined ? (
                  <Skeleton className="mt-3 h-8 w-12" />
                ) : (
                  <div className="mt-2 text-3xl font-bold tracking-tight">{mine[status]}</div>
                )}
                <p className="mt-1 text-xs text-muted-foreground">{hint}</p>
              </Link>
            ))}
          </div>
        </section>
      )}

      {canResearch && <CardStatsPanel />}

      <div className="grid gap-6 lg:grid-cols-7">
        <div className="flex flex-col gap-6 lg:col-span-4">
          {canResearch && <RecentCards currentUserId={user.id} />}
          {canSearch && canApprove && waiting > 0 && <WaitingForDecision waiting={waiting} />}
          {canSearch && canResearch && <RecentlyUpdated currentUserId={user.id} />}
        </div>

        {(canSearch || canResearch || canSeePortfolio) && (
          <Card className="flex flex-col gap-4 lg:col-span-3">
            <div>
              <CardTitle>Quick actions</CardTitle>
              <CardDescription>The usual way: search, save, check, ask.</CardDescription>
            </div>
            <div className="flex flex-col gap-3">
              {canSearch && (
                <QuickAction
                  to="/search"
                  icon={Search}
                  title="Search for a card"
                  text="Find every listing of a card on eBay and other websites."
                />
              )}
              {canResearch && (
                <QuickAction
                  to="/cards"
                  icon={ClipboardList}
                  title="Research"
                  text="Your cards and the listings found for them."
                />
              )}
              {canSeePortfolio && (
                <QuickAction
                  to="/portfolio"
                  icon={Library}
                  title="Portfolio"
                  text="Cards we own, with photos and details."
                />
              )}
            </div>
            <p className="mt-auto text-xs text-muted-foreground">
              Tip: press <kbd className="rounded border bg-muted px-1 font-mono">Ctrl K</kbd> to jump anywhere
              or search for a card.
            </p>
          </Card>
        )}
      </div>
    </div>
  );
}

function QuickAction({
  to,
  icon: Icon,
  title,
  text,
}: {
  to: "/search" | "/cards" | "/portfolio";
  icon: LucideIcon;
  title: string;
  text: string;
}) {
  return (
    <Link
      to={to}
      className="flex items-center gap-4 rounded-lg border p-4 transition-colors hover:bg-accent/50"
    >
      <span className="flex size-9 shrink-0 items-center justify-center rounded-md bg-muted">
        <Icon className="size-4" aria-hidden="true" />
      </span>
      <span className="min-w-0 flex-1">
        <span className="block text-sm font-semibold text-foreground">{title}</span>
        <span className="block text-sm text-muted-foreground">{text}</span>
      </span>
      <ArrowRight className="size-4 shrink-0 text-muted-foreground" aria-hidden="true" />
    </Link>
  );
}

/** For the admin: the requests waiting for a decision, oldest change first in the list below. */
function WaitingForDecision({ waiting }: { waiting: number }) {
  const list = useResearchList({ status: "waiting_approval", pageSize: RECENT });
  return (
    <Card className="flex flex-col gap-4 border-amber-300 dark:border-amber-500/30">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <CardTitle>
            {waiting === 1
              ? "1 request is waiting for your decision"
              : `${String(waiting)} requests are waiting for your decision`}
          </CardTitle>
          <CardDescription>Open Approvals to approve or reject.</CardDescription>
        </div>
        <Button variant="secondary" size="sm" asChild>
          <Link to="/approvals">Open Approvals</Link>
        </Button>
      </div>
      <RecordList records={list.data?.data} loading={list.isPending} showRequest />
    </Card>
  );
}

/** The cards changed most recently, with where their listings are. */
function RecentCards({ currentUserId }: { currentUserId: string }) {
  const list = useCardList({ pageSize: RECENT });
  return (
    <Card className="flex flex-col gap-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <CardTitle>Recent cards</CardTitle>
          <CardDescription>The latest cards in research across the team.</CardDescription>
        </div>
        <Button variant="ghost" size="sm" asChild>
          <Link to="/cards">
            View all
            <ArrowRight aria-hidden="true" />
          </Link>
        </Button>
      </div>
      {list.data?.total === 0 ? (
        <p className="text-sm text-muted-foreground">No cards yet. Click “Add card” to start.</p>
      ) : (
        <ul className="flex flex-col divide-y">
          {(list.data?.data ?? []).map((card) => (
            <li key={card.id} className="flex items-center gap-3 py-3 first:pt-0 last:pb-0">
              <div className="min-w-0 flex-1">
                <Link
                  to="/cards/$cardId"
                  params={{ cardId: card.id }}
                  className="block truncate text-sm font-medium text-foreground hover:underline"
                >
                  {card.name}
                </Link>
                <p className="truncate text-xs text-muted-foreground">
                  {card.addedBy.id === currentUserId ? "You" : card.addedBy.name} · {card.linkCount}{" "}
                  {card.linkCount === 1 ? "listing" : "listings"}
                </p>
              </div>
              <span className="text-xs text-muted-foreground">{describeCardStep(card)}</span>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}

function RecentlyUpdated({ currentUserId }: { currentUserId: string }) {
  const list = useResearchList({ status: "all", pageSize: RECENT });
  return (
    <Card className="flex flex-col gap-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <CardTitle>Recently updated</CardTitle>
          <CardDescription>The latest research changes across the team.</CardDescription>
        </div>
        <Button variant="ghost" size="sm" asChild>
          <Link to="/research" search={{ status: "all" }}>
            View all
            <ArrowRight aria-hidden="true" />
          </Link>
        </Button>
      </div>
      {list.data?.total === 0 ? (
        <p className="text-sm text-muted-foreground">
          Nothing yet. Search for a card and click “Save to Research” to start.
        </p>
      ) : (
        <RecordList records={list.data?.data} loading={list.isPending} currentUserId={currentUserId} />
      )}
    </Card>
  );
}

function RecordList({
  records,
  loading,
  showRequest = false,
  currentUserId,
}: {
  records: readonly ResearchRecord[] | undefined;
  loading: boolean;
  showRequest?: boolean;
  currentUserId?: string;
}) {
  if (loading || records === undefined) {
    return (
      <div className="flex flex-col gap-3">
        {Array.from({ length: 3 }, (_, i) => (
          <Skeleton key={i} className="h-10 w-full" />
        ))}
      </div>
    );
  }
  return (
    <ul className="flex flex-col divide-y">
      {records.map((record) => (
        <li key={record.id} className="flex items-center gap-3 py-3 first:pt-0 last:pb-0">
          {record.card.imageSmall === null ? (
            <div className="h-10 w-7 shrink-0 rounded bg-muted" aria-hidden="true" />
          ) : (
            <img
              src={record.card.imageSmall}
              alt=""
              loading="lazy"
              className="h-10 w-7 shrink-0 object-contain"
            />
          )}
          <div className="min-w-0 flex-1">
            <Link
              to="/research/$researchId"
              params={{ researchId: record.id }}
              className="block truncate text-sm font-medium text-foreground hover:underline"
            >
              {record.card.name} {record.card.number}
            </Link>
            <p className="truncate text-xs text-muted-foreground">
              {showRequest && record.request !== null
                ? `${record.owner.name} asks up to ${formatMoney({ amount: record.request.maxPriceUsd, currency: "USD" })}`
                : `${record.owner.id === currentUserId ? "You" : record.owner.name} · ${formatDate(record.updatedAt)}`}
            </p>
          </div>
          <Badge tone={RESEARCH_STATUS_TONES[record.status]}>{RESEARCH_STATUS_LABELS[record.status]}</Badge>
        </li>
      ))}
    </ul>
  );
}
